/**
 * What: Opens and closes a Shopify `s-modal` element from React state and reports when the merchant hides it.
 * Transport / data-flow: Outside-system helper (plan #4264, rule `effect-none-in-pages`). Waits for the custom
 * element, calls `showOverlay()` / `hideOverlay()` when `open` changes, and forwards `afterhide` to `onClose`.
 */
import { useEffect, useRef } from 'react'

type ShopifyModalElement = HTMLElement & { showOverlay(): void; hideOverlay(): void }

export function useShopifyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const modalRef = useRef<ShopifyModalElement>(null)

  useEffect(() => {
    if (open) {
      customElements.whenDefined('s-modal').then(() => modalRef.current?.showOverlay())
    } else {
      modalRef.current?.hideOverlay()
    }
  }, [open])

  useEffect(() => {
    const el = modalRef.current
    if (!el) return
    const onHide = () => onClose()
    el.addEventListener('afterhide', onHide)
    return () => el.removeEventListener('afterhide', onHide)
  }, [onClose])

  return modalRef
}
